class BitArray {
  constructor(size) {
    this.size = size;
    // Each Uint32 stores 32 bits, so we need size / 32 elements
    this.data = new Uint32Array(Math.ceil(size / 32));
  }

  /**
   * Sets the value (true/false) at the given index.
   * @param {number} index - The bit index to modify.
   * @param {boolean} value - The boolean value to set.
   */
  set(index, value) {
    const bucket = index >>> 5; // Equivalent to Math.floor(index / 32)
    const bit = index & 31; // Equivalent to index % 32
    if (value) {
      this.data[bucket] |= 1 << bit; // Set the bit to 1
    } else {
      this.data[bucket] &= ~(1 << bit); // Set the bit to 0
    }
  }

  /**
   * Gets the boolean value at the given index.
   * @param {number} index - The bit index to retrieve.
   * @returns {boolean} - The value at the given index.
   */
  get(index) {
    const bucket = index >>> 5;
    const bit = index & 31;
    return (this.data[bucket] & (1 << bit)) !== 0;
  }

  /**
   * Sets all values in the BitArray to the given boolean value.
   * @param {boolean} value - The value to set for all bits.
   */
  setAll(value) {
    const fillValue = value ? 0xffffffff : 0; // All bits to 1 or 0
    this.data.fill(fillValue);
  }

  /**
   * Iterates over each bit in the BitArray and executes a callback function.
   * @param {function(boolean, number): void} callback - Function to execute on each bit.
   */
  forEach(callback) {
    for (let i = 0; i < this.size; i++) {
      callback(this.get(i), i);
    }
  }

  /**
   * Counts the number of set bits (true values) in a given range.
   * @param {number} [start=0] - The starting index (inclusive).
   * @param {number} [count=this.size - start] - The number of bits to check.
   * @returns {number} - The count of true (1) bits in the range.
   */
  countSetBits(start = 0, count = this.size - start) {
    const end = Math.min(this.size, start + count);
    if (start >= end) return 0;

    let result = 0;
    for (let i = start; i < end; i++) {
      if (this.get(i)) result++;
    }

    return result;
  }

  /**
   * Returns the total size of the BitArray (number of bits).
   * @returns {number} - The total number of bits in the BitArray.
   */
  getSize() {
    return this.size;
  }

  /**
   * Checks if all values in the BitArray are false.
   * @returns {boolean} - True if all bits are false, otherwise false.
   */
  isAllFalse() {
    return this.data.every(bucket => bucket === 0);
  }

  /**
   * Creates a BitArray from a JSON object received from a Web Worker.
   * @param {Object} json - The JSON object containing size and data.
   * @returns {BitArray} - A new BitArray initialized with the received data.
   */
  static fromJSON(json) {
    const bitArray = new BitArray(json.size);
    bitArray.data = new Uint32Array(json.data); // Restore the Uint32Array
    return bitArray;
  }
}

export default BitArray;
